import {
  useRTCClient,
  useClientEvent,
  useRemoteUsers,
} from 'agora-rtc-react';
import { useCallback, useMemo, useState } from 'react';

import Button from "@ergeon/core-components/exports/Button.ts";

import { PhoneXMarkIcon } from "@heroicons/react/24/outline";

import ButtonCamera from '@/app/components/ButtonCamera';
import ButtonMicrophone from '@/app/components/ButtonMicrophone';
import ButtonScreenShare from '@/app/components/ButtonScreenShare';
import ButtonSettings from '@/app/components/ButtonSettings';
import ButtonSwitchCamera from '@/app/components/ButtonSwitchCamera';
import ButtonVirtualBackground from '@/app/components/ButtonVirtualBackground';
import { ScreenPreloader } from '@/app/components/Preloader';
import RecordingBadge from '@/app/components/RecordingBadge';
import Title from '@/app/components/Title';
import VideoTracksDisplay from '@/app/components/VideoTracksDisplay';
import { useAIDenoiserExtension } from '@/app/extensions/AIDenoiser/context';
import { useMeetingData, useUserData } from '@/app/queries/MeetingQuery/context';
import useOverlayVisibility from '@/app/hooks/useOverlayVisibility';
import useSoundEffects from '@/app/hooks/useSoundEffects';
import { cn } from '@/lib/utils';

import './MeetingView.css';

import { useAppContext } from '@/app/AppContext';

import usePublish from '@/app/hooks/usePublish';
import useAutoAdjustStreamQuality from '@/app/hooks/useAutoAdjustStreamQuality';
import AnalyticsSync from '@/app/analytics/components/AnalyticsSync';

import DialogFeedback from '@/app/components/DialogFeedback';

interface MeetingViewProps {
  // Tears the meeting down, once the feedback dialog is done.
  onEndCall(): void;
  // Fired first, the moment the user clicks "End call", so the host can promptly
  // wind up recording/transcription while both sides are still in the channel.
  // See MeetingPage.
  onBeforeEndCall(): void;
}

export default function MeetingView({ onEndCall, onBeforeEndCall }: MeetingViewProps) {
  const [showVideoTracks, setShowVideoTracks] = useState(true);
  const { isVisible, reveal } = useOverlayVisibility();
  const userData = useUserData();

  const remoteUsers = useRemoteUsers();
  const { playJoinSound } = useSoundEffects();
  const { participants } = useMeetingData();

  const knownUids = useMemo(() => new Set(
    participants.flatMap(p => [p.user_id, p.screen_share_id])
  ), [participants]);

  const agoraClient = useRTCClient();

  useAutoAdjustStreamQuality(agoraClient);

  useClientEvent(agoraClient, 'user-joined', (user) => {
    if (knownUids.has(user.uid as number)) {
      playJoinSound();
    }
  });

  const { camera, microphone } = useAppContext();

  const onEndCallHandler = useCallback(() => {
    // Signal the host-side recording lifecycle to end promptly (see MeetingPage),
    // then tear down local devices.
    onBeforeEndCall();
    if (microphone.track?.isPlaying) {
      microphone.track.stop();
    }
    if (microphone.isEnabled) {
      microphone.toggleDevice()
    }
    if (camera.track?.isPlaying) {
      camera.track.stop();
    }
    if (camera.isEnabled) {
      camera.toggleDevice()
    }
    setShowVideoTracks(false);
  }, [microphone, camera, onBeforeEndCall]);

  const isCameraReady = !(camera.isLoading || camera.error);
  const isMicReady = !(microphone.isLoading || microphone.error);

  const isLoading = camera.isLoading || microphone.isLoading;

  usePublish(microphone.track, isMicReady);
  usePublish(camera.track, isCameraReady);

  useAIDenoiserExtension(
    { track: microphone.track },
    userData.isClient && !isMicReady
  );

  if (isLoading) {
    return <ScreenPreloader />;
  }

  // Floating over the video, the overlays need z-30 to clear the layer the
  // Agora SDK puts over a remote video (it wraps the track children in a
  // full-size z-index: 2 box), or the controls are visible but unclickable.
  //
  // They also fade out when left alone — but only on a phone, where the video
  // needs every pixel. A desktop has room for both, and hiding the buttons
  // there cost the AM team a day of thinking the call was broken (CAD-6881).
  const overlayClassName = cn(
    'absolute inset-x-0 z-30 flex px-4 transition-opacity duration-300 md:static',
    { 'pointer-events-none opacity-0 md:pointer-events-auto md:opacity-100': !isVisible },
  );

  return (
    <div
      className="dark fixed inset-0 h-dvh overflow-hidden bg-gray-900 md:flex md:flex-col"
      onPointerDown={reveal}
      onPointerMove={reveal}>
      <AnalyticsSync />

      <header className={cn(overlayClassName, 'top-0 items-center gap-3 bg-gradient-to-b from-black/70 to-transparent pb-10 pt-safe-top md:bg-none md:py-3')}>
        <Title />
        <RecordingBadge />
      </header>

      <VideoTracksDisplay {...{ localCameraTrack: camera.track!, remoteUsers, showVideoTracks }} />

      <div className={cn(overlayClassName, 'bottom-0 items-center justify-center gap-6 bg-gradient-to-t from-black/70 to-transparent pb-safe-bottom pt-12 md:bg-none md:py-4')}>
        <div className="flex items-center gap-3">
          {userData.isClient && <ButtonSwitchCamera />}
          <ButtonMicrophone />
          <ButtonCamera />
          {userData.isHost &&
            <>
              <ButtonScreenShare />
              <ButtonVirtualBackground localCameraTrack={camera.track!} />
            </>
          }
          <ButtonSettings />
        </div>
        <DialogFeedback onEndCall={onEndCall}>
          <Button
            disabled={!showVideoTracks}
            flavor="danger"
            isSquare
            onClick={onEndCallHandler}
            size="large"
            title="End call">
            <PhoneXMarkIcon className="size-6" />
          </Button>
        </DialogFeedback>
      </div>
    </div>
  );
}
